import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ChevronDown, Minus , Check } from "lucide-react";
import clsx from "clsx";

export type SelectScope = "all" | "visible" | "none";

type Props = {
  disabled?: boolean;
  /** true | false | indeterminate visual */
  checked: boolean | "indeterminate";
  /** Which menu item is active. Partial row selection uses "none" so neither All nor Visible is marked. */
  scope: SelectScope;
  onScopeSelect: (scope: SelectScope) => void;
};

/**
 * Gmail-style header control: checkbox state + menu (All / Visible / None).
 */
export function SelectScopeHeaderControl({
  disabled,
  checked,
  scope,
  onScopeSelect,
}: Props) {
  const isIndeterminate = checked === "indeterminate";
  const isChecked = checked === true;

  return (
    <div
      className={clsx(
        "inline-flex items-center rounded-md overflow-hidden",
        disabled && "opacity-50 pointer-events-none",
      )}
    >
      <button
        type="button"
        className="flex h-8 w-8 items-center justify-center hover:bg-[#E5E7EB]"
        disabled={disabled}
        onClick={() => {
          if (isChecked || isIndeterminate) onScopeSelect("none");
          else onScopeSelect("visible");
        }}
        aria-label={
          isChecked || isIndeterminate ? "Clear selection" : "Select visible"
        }
      >
        {isIndeterminate ? (
          <span className="flex h-4 w-4 items-center justify-center rounded-sm border border-primary bg-primary text-primary-foreground">
            <Minus className="h-3 w-3" />
          </span>
        ) : (
          <Checkbox
            checked={isChecked}
            className="pointer-events-none"
            tabIndex={-1}
          />
        )}
      </button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className="flex h-8 w-4 items-center justify-center hover:bg-[#E5E7EB]"
            disabled={disabled}
            aria-label="Selection options"
          >
            <ChevronDown className="h-3.5 w-3.5 text-[#4B5563]" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="min-w-[140px]">
          <DropdownMenuItem onClick={() => onScopeSelect("all")}>
            All {scope === "all" && <Check className="h-3 w-3" />}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => onScopeSelect("visible")}>
            Visible {scope === "visible" && <Check className="h-3 w-3" />}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => onScopeSelect("none")}>
            None
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
